import { Button, Modal, Text, Tooltip, TooltipContent, TooltipTrigger } from '@viapeople/components'
import { useMemo, useState } from 'react'

interface InteractionParticipant {
	userId: string
	firstName: string
	lastName: string
	email: string
	completedCount: number
	naCount: number
	pendingCount: number
}

interface InteractionCell {
	raterId: string
	rateeId: string
	status: 'completed' | 'na' | 'pending'
}

interface NaResponse {
	raterId: string
	raterName: string
	rateeId: string
	rateeName: string
}

interface PecInteractionGraphProps {
	participants: InteractionParticipant[]
	interactions: InteractionCell[]
	naResponses: NaResponse[]
	filterByRaterId: string | null
	filterByRateeId: string | null
	onFilterByRaterChange: (raterId: string | null) => void
	onFilterByRateeChange: (rateeId: string | null) => void
}

// Get color for interaction status - only highlight non-interactions (N/A) in red
const getStatusColor = (status: 'completed' | 'na' | 'pending' | 'self'): string => {
	switch (status) {
		case 'na':
			return 'bg-red-500'
		case 'self':
			return 'bg-gray-200'
		default:
			return 'bg-gray-50'
	}
}

// Get initials for compact display
const getInitials = (firstName: string, lastName: string): string => {
	return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}

export const PecInteractionGraph = ({
	participants,
	interactions,
	naResponses,
	filterByRaterId,
	filterByRateeId,
	onFilterByRaterChange,
	onFilterByRateeChange,
}: PecInteractionGraphProps) => {
	const [isFullscreen, setIsFullscreen] = useState(false)

	// Build a lookup map for quick interaction status access
	const interactionMap = useMemo(() => {
		const map = new Map<string, 'completed' | 'na' | 'pending'>()
		interactions.forEach((i) => {
			map.set(`${i.raterId}-${i.rateeId}`, i.status)
		})
		return map
	}, [interactions])

	// Get interaction status for a specific rater-ratee pair
	const getInteractionStatus = (raterId: string, rateeId: string): 'completed' | 'na' | 'pending' | 'self' => {
		if (raterId === rateeId) return 'self'
		return interactionMap.get(`${raterId}-${rateeId}`) ?? 'pending'
	}

	// Filter N/A responses based on current filters
	const filteredNaResponses = useMemo(() => {
		let filtered = naResponses
		if (filterByRaterId) {
			filtered = filtered.filter((r) => r.raterId === filterByRaterId)
		}
		if (filterByRateeId) {
			filtered = filtered.filter((r) => r.rateeId === filterByRateeId)
		}
		return filtered
	}, [naResponses, filterByRaterId, filterByRateeId])

	// Calculate totals
	const totalNaCount = naResponses.length
	const totalCompleted = interactions.filter((i) => i.status === 'completed').length
	// Count users who have at least one pending response, not total pending interactions
	const usersWithPending = participants.filter((p) => p.pendingCount > 0).length
	// Count unique participants who gave N/A responses
	const participantsWhoGaveNa = new Set(naResponses.map((r) => r.raterId)).size
	// Count unique participants who received N/A responses
	const participantsReceivingNa = new Set(naResponses.map((r) => r.rateeId)).size

	if (participants.length === 0) {
		return <div className='text-center py-8 text-gray-500'>No participants in this cycle</div>
	}

	// Extracted content to render in both inline and fullscreen views
	const interactionContent = (isFullscreenView: boolean) => (
		<div className={isFullscreenView ? 'space-y-6 overflow-auto h-full p-4' : 'space-y-6'}>
			{/* Summary Stats */}
			<div className='grid grid-cols-5 gap-4'>
				<div className='bg-green-50 border border-green-200 rounded-lg p-4 text-center'>
					<Text variant='h4' weight='semibold' className='text-green-700'>
						{totalCompleted}
					</Text>
					<Text variant='small' className='text-green-600'>
						Completed Ratings
					</Text>
				</div>
				<div className='bg-red-50 border border-red-200 rounded-lg p-4 text-center'>
					<Text variant='h4' weight='semibold' className='text-red-700'>
						{totalNaCount}
					</Text>
					<Text variant='small' className='text-red-600'>
						Non-Interactions
					</Text>
				</div>
				<div className='bg-red-50 border border-red-200 rounded-lg p-4 text-center'>
					<Text variant='h4' weight='semibold' className='text-red-700'>
						{participantsWhoGaveNa}
						<span className='text-sm font-normal text-red-500'> / {participants.length}</span>
					</Text>
					<Text variant='small' className='text-red-600'>
						Reported No Interaction
					</Text>
				</div>
				<div className='bg-red-50 border border-red-200 rounded-lg p-4 text-center'>
					<Text variant='h4' weight='semibold' className='text-red-700'>
						{participantsReceivingNa}
						<span className='text-sm font-normal text-red-500'> / {participants.length}</span>
					</Text>
					<Text variant='small' className='text-red-600'>
						Not Interacted With
					</Text>
				</div>
				<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center'>
					<Text variant='h4' weight='semibold' className='text-yellow-700'>
						{usersWithPending}
						<span className='text-sm font-normal text-yellow-600'> / {participants.length}</span>
					</Text>
					<Text variant='small' className='text-yellow-600'>
						Users with Pending
					</Text>
				</div>
			</div>

			{/* Filters */}
			<div className='bg-white border border-gray-200 rounded-lg p-4'>
				<Text variant='small' weight='semibold' className='text-gray-700 mb-3 block'>
					Filter Non-Interactions
				</Text>
				<div className='flex gap-4'>
					<div className='flex-1'>
						<label className='block text-xs text-gray-500 mb-1' htmlFor='filter-by-rater'>
							By Person (who reported no interaction)
						</label>
						<select
							id='filter-by-rater'
							className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm'
							value={filterByRaterId ?? ''}
							onChange={(e) => onFilterByRaterChange(e.target.value || null)}>
							<option value=''>All Participants</option>
							{participants.map((p) => (
								<option key={p.userId} value={p.userId}>
									{p.firstName} {p.lastName} ({p.naCount} non-interactions)
								</option>
							))}
						</select>
					</div>
					<div className='flex-1'>
						<label className='block text-xs text-gray-500 mb-1' htmlFor='filter-by-ratee'>
							By Person (who was not interacted with)
						</label>
						<select
							id='filter-by-ratee'
							className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm'
							value={filterByRateeId ?? ''}
							onChange={(e) => onFilterByRateeChange(e.target.value || null)}>
							<option value=''>All Participants</option>
							{participants.map((p) => (
								<option key={p.userId} value={p.userId}>
									{p.firstName} {p.lastName}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			{/* Non-Interactions Table */}
			<div className='bg-white border border-gray-200 rounded-lg p-4'>
				<div className='mb-4'>
					<Text variant='medium' weight='semibold' className='text-gray-900'>
						Non-Interactions ({filteredNaResponses.length})
					</Text>
					<Text variant='small' className='text-gray-600 mt-1'>
						Participants who haven't interacted with each other (marked N/A)
					</Text>
				</div>

				{filteredNaResponses.length === 0 ? (
					<div className='text-center py-6 text-gray-500'>
						<Text variant='small'>
							{naResponses.length === 0
								? 'No non-interactions reported in this cycle'
								: 'No non-interactions match the current filters'}
						</Text>
					</div>
				) : (
					<div className='overflow-auto max-h-[300px]'>
						<table className='min-w-full divide-y divide-gray-200'>
							<thead className='bg-gray-50 sticky top-0'>
								<tr>
									<th className='px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase'>Person</th>
									<th className='px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase'>&rarr;</th>
									<th className='px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase'>
										Didn't Interact With
									</th>
								</tr>
							</thead>
							<tbody className='divide-y divide-gray-200'>
								{filteredNaResponses.map((na) => (
									<tr key={`${na.raterName}-${na.rateeName}`} className='hover:bg-gray-50'>
										<td className='px-4 py-2'>
											<Text variant='small'>{na.raterName}</Text>
										</td>
										<td className='px-4 py-2 text-center'>
											<span className='inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium'>
												N/A
											</span>
										</td>
										<td className='px-4 py-2'>
											<Text variant='small'>{na.rateeName}</Text>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Interaction Matrix */}
			<div className='bg-white border border-gray-200 rounded-lg p-4'>
				<div className='mb-4'>
					<Text variant='medium' weight='semibold' className='text-gray-900'>
						Interaction Matrix
					</Text>
					<Text variant='small' className='text-gray-600 mt-1'>
						Visual overview of interactions between participants. Red cells indicate no interaction (N/A).
					</Text>
				</div>

				{/* Legend */}
				<div className='flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg'>
					<div className='flex items-center gap-2'>
						<span className='w-6 h-6 rounded bg-gray-50 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400'>
							-
						</span>
						<Text variant='xsmall' className='text-gray-600'>
							Interacted (rated or pending)
						</Text>
					</div>
					<div className='flex items-center gap-2'>
						<span className='w-6 h-6 rounded bg-red-500 flex items-center justify-center text-[10px] text-white font-semibold'>
							N/A
						</span>
						<Text variant='xsmall' className='text-gray-600'>
							No Interaction
						</Text>
					</div>
				</div>

				{/* Matrix Table */}
				<div className='overflow-auto max-h-[500px]'>
					<table className='min-w-full border-collapse'>
						<thead className='sticky top-0 z-20 bg-white'>
							<tr>
								{/* Empty corner cell */}
								<th className='sticky left-0 z-30 bg-gray-100 border-b border-r border-gray-300 p-2 min-w-[120px]'>
									<div className='flex flex-col items-start'>
										<Text variant='xsmall' className='text-gray-500'>
											Ratee &rarr;
										</Text>
										<Text variant='xsmall' className='text-gray-500'>
											Rater &darr;
										</Text>
									</div>
								</th>
								{/* Column headers (Ratees) */}
								{participants.map((ratee) => (
									<th
										key={ratee.userId}
										className='border-b border-r border-gray-300 p-1 min-w-[40px] bg-gray-50'
										title={`${ratee.firstName} ${ratee.lastName}`}>
										<div className='flex flex-col items-center cursor-help'>
											<Text variant='xsmall' weight='semibold' className='text-gray-700'>
												{getInitials(ratee.firstName, ratee.lastName)}
											</Text>
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{participants.map((rater) => (
								<tr key={rater.userId} className='hover:bg-gray-50'>
									{/* Row header (Rater name) */}
									<td
										className='sticky left-0 z-10 bg-white border-b border-r border-gray-300 p-2'
										title={`${rater.email}\nCompleted: ${rater.completedCount}, N/A: ${rater.naCount}, Pending: ${rater.pendingCount}`}>
										<div className='flex items-center gap-2 cursor-help'>
											<div className='w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center'>
												<Text variant='xsmall' weight='semibold' className='text-primary-700'>
													{getInitials(rater.firstName, rater.lastName)}
												</Text>
											</div>
											<div className='truncate max-w-[80px]'>
												<Text variant='xsmall' weight='semibold' className='text-gray-900'>
													{rater.firstName}
												</Text>
											</div>
										</div>
									</td>
									{/* Status cells */}
									{participants.map((ratee) => {
										const status = getInteractionStatus(rater.userId, ratee.userId)
										const colorClass = getStatusColor(status)
										const raterFullName = `${rater.firstName} ${rater.lastName}`
										const rateeFullName = `${ratee.firstName} ${ratee.lastName}`

										const tooltipContent =
											status === 'self' ? (
												<span className='text-gray-300'>Self-rating excluded</span>
											) : status === 'completed' ? (
												<div className='text-left'>
													<div className='font-semibold text-green-400'>✓ Rated</div>
													<div className='text-gray-300'>
														{raterFullName} → {rateeFullName}
													</div>
													<div className='text-gray-400 text-[10px]'>Rating has been submitted</div>
												</div>
											) : status === 'na' ? (
												<div className='text-left'>
													<div className='font-semibold text-red-400'>✗ No Interaction</div>
													<div className='text-gray-300'>
														{raterFullName} → {rateeFullName}
													</div>
													<div className='text-gray-400 text-[10px]'>These participants haven't interacted</div>
												</div>
											) : (
												<div className='text-left'>
													<div className='font-semibold text-yellow-400'>⏳ Pending</div>
													<div className='text-gray-300'>
														{raterFullName} → {rateeFullName}
													</div>
													<div className='text-gray-400 text-[10px]'>Awaiting rating submission</div>
												</div>
											)

										return (
											<td key={ratee.userId} className='border-b border-r border-gray-200 p-0'>
												<Tooltip>
													<TooltipTrigger asChild>
														<div className={`w-full h-8 flex items-center justify-center cursor-help ${colorClass}`}>
															{status === 'na' ? (
																<Text variant='xsmall' weight='semibold' className='text-white'>
																	N/A
																</Text>
															) : (
																<Text variant='xsmall' className='text-gray-400'>
																	-
																</Text>
															)}
														</div>
													</TooltipTrigger>
													<TooltipContent side='top' className='z-50'>
														{tooltipContent}
													</TooltipContent>
												</Tooltip>
											</td>
										)
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)

	return (
		<div>
			{/* Expand to fullscreen button */}
			<div className='flex justify-end mb-4'>
				<Button variant='secondary' size='small' onClick={() => setIsFullscreen(true)}>
					⛶ Expand Full Screen
				</Button>
			</div>

			{/* Inline content */}
			{interactionContent(false)}

			{/* Fullscreen modal */}
			<Modal
				isOpen={isFullscreen}
				onClose={() => setIsFullscreen(false)}
				title='Interaction Graph'
				fullScreen
				fullContent
				bodyClassName='bg-gray-50'>
				{interactionContent(true)}
			</Modal>
		</div>
	)
}
